import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useSEO } from "@/components/SEO";
import { Eyebrow, Masked, Reveal } from "@/components/bits";
import { FunnelV, Waterfall } from "@/components/charts";
import ExecDashboard from "@/components/ExecDashboard";
import CTASection from "@/components/CTASection";
import { SEO, SOLUTIONS } from "@/lib/site";

const ScoreRow = ({ name, score, tag }) => (
    <div className="flex items-center gap-4 border-b border-ink/[0.07] py-3 last:border-0" data-testid={`solution-account-row-${name}`}>
        <span className="w-28 font-mono text-[10px] uppercase tracking-[0.14em] text-slategray">{name}</span>
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink/[0.07]">
            <motion.div className="h-full rounded-full bg-vantera" initial={{ width: 0 }} whileInView={{ width: `${score}%` }} viewport={{ once: true }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }} />
        </div>
        <span className="font-mono text-[10px] text-vantera">{score}</span>
        <span className="hidden rounded-full border border-azure/40 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-azure sm:inline">{tag}</span>
    </div>
);

const SolutionViz = ({ viz }) => {
    switch (viz) {
        case "accounts":
            return (
                <div className="rounded-xl border border-ink/10 bg-white p-6">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slategray">Account scoring — ICP fit & buying signals</p>
                    <div className="mt-4">
                        <ScoreRow name="Account 001" score={91} tag="High" />
                        <ScoreRow name="Account 002" score={78} tag="Watch" />
                        <ScoreRow name="Account 003" score={64} tag="Nurture" />
                        <ScoreRow name="Account 004" score={83} tag="High" />
                    </div>
                </div>
            );
        case "demand":
            return (
                <div className="rounded-xl border border-ink/10 bg-white p-6">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slategray">Coordinated channels</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {["Paid Search", "LinkedIn", "ABM Campaigns", "Retargeting", "Executive Outbound", "Content / Search", "Channel"].map((c) => (
                            <span key={c} className="rounded-full border border-ink/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-ink">{c}</span>
                        ))}
                    </div>
                    <p className="mt-5 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em] text-vantera">Optimized for <span className="rounded-md bg-vantera px-2.5 py-1 text-ink">Qualified pipeline · Revenue</span></p>
                </div>
            );
        case "followup":
            return (
                <div className="scan-sweep rounded-xl border border-ink/10 bg-white p-6">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slategray">AI-assisted response flow — human oversight</p>
                    <div className="mt-4 space-y-3">
                        {["Respond", "Qualify", "Route", "Schedule", "Follow up", "Update CRM"].map((s, i) => (
                            <motion.p key={s} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="flex items-center gap-3 text-[15px] font-medium text-ink">
                                <Check size={13} strokeWidth={3} className="text-vantera" aria-hidden="true" /> {s}
                            </motion.p>
                        ))}
                    </div>
                    <p className="mt-4 inline-block rounded-md bg-ink px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-vantera">Next best action → assigned</p>
                </div>
            );
        case "recovery":
            return (
                <div className="rounded-xl border border-ink/10 bg-white p-6">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slategray">Reactivation paths</p>
                    <div className="mt-4 space-y-3">
                        {[["Expired quote", "Re-quote"], ["Dormant customer", "Win-back"], ["Old lead", "Revive"], ["Lapsed buyer", "Renew"]].map(([a, b], i) => (
                            <motion.p key={a} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="flex items-center justify-between rounded-lg bg-mist px-4 py-3 text-[14px]">
                                <span className="font-mono text-[11px] uppercase tracking-wider text-slategray">{a}</span>
                                <span className="text-vantera" aria-hidden="true">→</span>
                                <span className="font-display font-bold text-ink">{b}</span>
                            </motion.p>
                        ))}
                    </div>
                </div>
            );
        case "funnel":
            return (
                <div className="rounded-xl border border-ink/10 bg-white p-6">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slategray">Advertising → gross profit connection</p>
                    <div className="mt-4">
                        <FunnelV dark={false} steps={[{ n: "Advertising spend", v: 100 }, { n: "Lead", v: 26 }, { n: "Qualified opportunity", v: 9 }, { n: "Sale", v: 4 }, { n: "Gross profit", v: 4 }]} />
                    </div>
                    <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.16em] text-slategray">Illustrative flow — not actual client results</p>
                </div>
            );
        case "waterfall":
            return (
                <div className="rounded-xl border border-ink/10 bg-white p-6">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slategray">Margin bridge — illustrative</p>
                    <div className="mt-4">
                        <Waterfall dark={false} steps={[{ n: "PRICE", delta: 12 }, { n: "DISCOUNT", delta: -18 }, { n: "VENDOR", delta: 8 }, { n: "ADMIN EFF", delta: 5 }, { n: "MARGIN", delta: 0 }]} />
                    </div>
                </div>
            );
        default:
            return null;
    }
};

const Solutions = () => {
    useSEO({ title: SEO["/solutions"].title, description: SEO["/solutions"].description, path: "/solutions" });

    return (
        <>
            <section className="relative overflow-hidden pt-32 pb-16 sm:pt-40 sm:pb-20" data-testid="solutions-hero">
                <div className="bg-grid-light absolute inset-0 [mask-image:radial-gradient(70%_60%_at_40%_20%,black,transparent)]" aria-hidden="true" />
                <div className="container-x relative">
                    <Eyebrow>Solutions</Eyebrow>
                    <Masked
                        lines={["Increase Revenue.", "Recover What You Already Earned.", "Reduce What You", "Shouldn't Be Spending."]}
                        className="mt-7 max-w-5xl font-display text-4xl font-extrabold leading-[1.06] tracking-tightest sm:text-6xl lg:text-[68px]"
                        itemClassName="[&:nth-child(2)]:text-vantera"
                    />
                    <p className="mt-8 max-w-2xl text-lg leading-relaxed text-slategray sm:text-xl">
                        Vantera attacks profitable growth from both sides of the P&L: increasing revenue and reducing unnecessary cost.
                    </p>
                </div>
            </section>

            <section className="pb-24 sm:pb-32" data-testid="solutions-list">
                <div className="container-x space-y-20 sm:space-y-28">
                    {SOLUTIONS.filter((s) => s.viz !== "exec").map((s, i) => (
                        <div key={s.num} className={`grid items-center gap-10 lg:grid-cols-2 lg:gap-16 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`} data-testid={`solution-${s.num}`}>
                            <div>
                                <p className="text-outline font-display text-6xl font-extrabold leading-none">{s.num}</p>
                                <h2 className="mt-5 font-display text-2xl font-extrabold tracking-tight sm:text-4xl">{s.name}</h2>
                                <p className="mt-5 max-w-xl text-[17px] leading-relaxed text-slategray">{s.desc}</p>
                            </div>
                            <Reveal delay={0.1}>
                                <SolutionViz viz={s.viz} />
                            </Reveal>
                        </div>
                    ))}
                </div>
            </section>

            <section className="border-t border-ink/10 bg-paper py-24 sm:py-32" data-testid="solutions-exec">
                <div className="container-x grid items-start gap-12 lg:grid-cols-[0.9fr_1.4fr]">
                    <div className="lg:sticky lg:top-28">
                        <p className="text-outline font-display text-6xl font-extrabold leading-none">07</p>
                        <h2 className="mt-5 font-display text-2xl font-extrabold tracking-tight sm:text-4xl">AI Revenue & Executive Intelligence</h2>
                        <p className="mt-5 text-[17px] leading-relaxed text-slategray">{SOLUTIONS[6].desc}</p>
                    </div>
                    <ExecDashboard />
                </div>
            </section>

            <CTASection heading={["Talk to Vantera.", "See What the System", "Would Do First."]} sub="Start with an Executive Review — or go straight to the Business X-Ray." primary="Request a Business X-Ray" secondary="Book an Executive Review" testid="solutions-final-cta" />
        </>
    );
};

export default Solutions;